import api from './api';
import {
  User,
  Country,
  FraudFlag,
  ApiKey,
  Role,
  PermissionCatalogue,
  TransactionListResponse,
  AdminSetting,
} from '../types';
import {
  DAILY_PASS_MAX,
  DAILY_PASS_MIN,
  DAILY_PASS_SETTING_KEY,
  isValidDailyPassCount,
} from '../utils/adminSettings';

export const adminService = {
  async getPartnerUnlockReviews(): Promise<{
    review_required: { unlock_id: string; partner: string; workspace: string; stake_tp: number;
      user_id: number; game_id: number | null; pro_session_id: number | null }[];
    unmatched_callbacks: number;
  }> {
    const response = await api.get('/admin/partner-unlocks');
    return response.data;
  },
  async getAdscendReviews(): Promise<{
    review_required: { attempt_id: string; reward: string; status: string; user_id: number;
      pro_session_id: number | null; granted_at: string | null }[];
    unmatched_postbacks: number;
  }> {
    const response = await api.get('/admin/adscend-attempts');
    return response.data;
  },
  // Users
  async getUsers(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<User[]> {
    const response = await api.get<User[]>('/admin/users', { params });
    return response.data;
  },

  async getUser(userId: number): Promise<User> {
    const response = await api.get<User>(`/admin/users/${userId}`);
    return response.data;
  },

  async updateUserRole(userId: number, roleName: string): Promise<void> {
    await api.patch(`/admin/users/${userId}/role`, null, {
      params: { role_name: roleName },
    });
  },

  async updateUserStatus(userId: number, isActive: boolean): Promise<void> {
    await api.patch(`/admin/users/${userId}/status`, null, {
      params: { is_active: isActive },
    });
  },

  async updateUser(userId: number, data: {
    full_name?: string;
    email?: string;
    phone_number?: string;
    password?: string;
  }): Promise<User> {
    const response = await api.patch<User>(`/admin/users/${userId}`, data);
    return response.data;
  },

  // Countries/Config
  async getCountries(): Promise<Country[]> {
    const response = await api.get('/admin/countries');
    // Backend returns { success, message, data, total }
    // Extract the data array
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    // Fallback: if response is already an array, return it
    if (Array.isArray(response.data)) {
      return response.data;
    }
    // Return empty array if structure is unexpected
    return [];
  },

  async getCountry(countryId: number): Promise<Country> {
    const response = await api.get(`/admin/countries/${countryId}`);
    return response.data;
  },

  async updateCountry(countryId: number, data: Partial<Country>): Promise<Country> {
    const response = await api.patch(`/admin/countries/${countryId}`, data);
    return response.data;
  },

  // Fraud Management
  async getFraudFlags(): Promise<FraudFlag[]> {
    const response = await api.get('/admin/fraud-flags');
    return response.data;
  },

  async resolveFraudFlag(flagId: number): Promise<void> {
    await api.post(`/admin/fraud-flags/${flagId}/resolve`);
  },

  async getDuplicateReport(): Promise<any> {
    const response = await api.get('/admin/duplicate-detection/report');
    return response.data;
  },

  async getUserDuplicates(userId: number): Promise<any> {
    const response = await api.get(`/admin/duplicate-detection/users/${userId}/duplicates`);
    return response.data;
  },

  async mergeUsers(userId: number, duplicateIds: number[]): Promise<void> {
    await api.post(`/admin/duplicate-detection/users/${userId}/merge`, {
      duplicate_user_ids: duplicateIds,
    });
  },

  async runFraudScan(): Promise<any> {
    const response = await api.post('/admin/backup-fraud/fraud/scan');
    return response.data;
  },

  async getFraudReport(): Promise<any> {
    const response = await api.get('/admin/backup-fraud/fraud/report');
    return response.data;
  },

  async analyzeUserFraudRisk(userId: number): Promise<any> {
    const response = await api.get(`/admin/backup-fraud/fraud/users/${userId}/analysis`);
    return response.data;
  },

  // Transactions
  async getTransactions(params?: {
    page?: number;
    page_size?: number;
    user_id?: number;
    type_filter?: string;
    status_filter?: string;
  }): Promise<TransactionListResponse> {
    const response = await api.get('/transactions/admin/all', { params });
    const data = response.data;

    if (data && Array.isArray(data.transactions)) {
      return data as TransactionListResponse;
    }
    if (data && data.data && Array.isArray(data.data.transactions)) {
      return data.data as TransactionListResponse;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return { transactions: data.data, total: data.total ?? data.data.length, page: params?.page ?? 1, page_size: params?.page_size ?? 20 };
    }
    if (Array.isArray(data)) {
      return { transactions: data, total: data.length, page: params?.page ?? 1, page_size: params?.page_size ?? 20 };
    }

    return { transactions: [], total: 0, page: 1, page_size: 20 };
  },

  // Transaction Integrity
  async verifyTransaction(transactionId: number): Promise<any> {
    const response = await api.get(`/admin/transaction-integrity/verify/transaction/${transactionId}`);
    return response.data;
  },

  async verifyUserWallet(userId: number): Promise<any> {
    const response = await api.get(`/admin/transaction-integrity/verify/user/${userId}/wallet`);
    return response.data;
  },

  async verifyUserChain(userId: number): Promise<any> {
    const response = await api.get(`/admin/transaction-integrity/verify/user/${userId}/chain`);
    return response.data;
  },

  async getCorruptedTransactions(): Promise<any> {
    const response = await api.get('/admin/transaction-integrity/audit/corrupted-transactions');
    return response.data;
  },

  async repairUser(userId: number): Promise<any> {
    const response = await api.post(`/admin/transaction-integrity/repair/user/${userId}`);
    return response.data;
  },

  // API Keys
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await api.get('/admin/api-keys/list');
    return response.data;
  },

  async createApiKey(keyName: string, expiresInDays?: number): Promise<ApiKey> {
    const response = await api.post('/admin/api-keys/create', {
      key_name: keyName,
      expires_in_days: expiresInDays,
    });
    return response.data;
  },

  async rotateApiKey(keyId: number): Promise<ApiKey> {
    const response = await api.post(`/admin/api-keys/${keyId}/rotate`);
    return response.data;
  },

  async deleteApiKey(keyId: number): Promise<void> {
    await api.delete(`/admin/api-keys/${keyId}`);
  },

  // Metrics
  async getMetrics(): Promise<string> {
    const response = await api.get('/metrics', {
      responseType: 'text',
    });
    return response.data;
  },

  // Withdrawals
  async getAllWithdrawals(params?: {
    status_filter?: string;
    skip?: number;
    limit?: number;
  }): Promise<any> {
    const response = await api.get('/withdrawals/admin/all', { params });
    return response.data;
  },

  async getPendingWithdrawals(): Promise<any> {
    const response = await api.get('/withdrawals/admin/pending');
    return response.data;
  },

  async updateWithdrawalStatus(withdrawalId: number, data: {
    status: string;
    admin_notes?: string;
  }): Promise<any> {
    const response = await api.patch(`/withdrawals/admin/${withdrawalId}/status`, data);
    return response.data;
  },

  // Roles (if endpoint exists)
  // ============ RBAC ============
  async getRoles(): Promise<Role[]> {
    const response = await api.get('/admin/rbac/roles');
    return response.data;
  },

  async getPermissionCatalogue(): Promise<PermissionCatalogue> {
    const response = await api.get('/admin/rbac/permissions');
    return response.data;
  },

  async createRole(payload: {
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<Role> {
    const response = await api.post('/admin/rbac/roles', payload);
    return response.data;
  },

  async setRolePermissions(roleId: number, permissions: string[]): Promise<Role> {
    const response = await api.put(`/admin/rbac/roles/${roleId}/permissions`, {
      permissions,
    });
    return response.data;
  },

  async deleteRole(roleId: number): Promise<void> {
    await api.delete(`/admin/rbac/roles/${roleId}`);
  },

  // ============ ANALYTICS ============
  
  async getAnalyticsSummary(days: number = 7): Promise<any> {
    const response = await api.get('/admin/analytics/summary', { params: { days } });
    return response.data;
  },

  async getDailyActiveUsers(days: number = 7): Promise<any[]> {
    const response = await api.get('/admin/analytics/daily-active-users', { params: { days } });
    return response.data;
  },

  async getAdImpressions(days: number = 7): Promise<any[]> {
    const response = await api.get('/admin/analytics/ad-impressions', { params: { days } });
    return response.data;
  },

  async getHouseMargins(days: number = 7): Promise<any[]> {
    const response = await api.get('/admin/analytics/house-margins', { params: { days } });
    return response.data;
  },

  async getGameAnalytics(days: number = 7): Promise<any[]> {
    const response = await api.get('/admin/analytics/game-analytics', { params: { days } });
    return response.data;
  },

  async getUserEngagement(days: number = 7): Promise<any[]> {
    const response = await api.get('/admin/analytics/user-engagement', { params: { days } });
    return response.data;
  },

  async getQuizAnalytics(days: number = 30): Promise<any> {
    const response = await api.get('/admin/analytics/quiz-analytics', { params: { days } });
    return response.data;
  },

  async getExpandedAnalytics(days: number = 30): Promise<any> {
    const response = await api.get('/admin/analytics/expanded-summary', { params: { days } });
    return response.data;
  },

  // ============ SYSTEM STATUS ============
  
  async getRedisStatus(): Promise<any> {
    const response = await api.get('/api/v1/admin/redis/status');
    return response.data;
  },

  async healthCheck(): Promise<any> {
    const response = await api.get('/health');
    return response.data;
  },

  // ============ CAMPAIGNS (AIS / Neural Recall) ============

  async getCampaigns(params?: { active_only?: boolean; country?: string }): Promise<any> {
    const response = await api.get('/admin/campaigns', { params });
    return response.data;
  },

  async getCampaign(campaignId: string): Promise<any> {
    const response = await api.get(`/admin/campaigns/${campaignId}`);
    return response.data;
  },

  async createCampaign(data: {
    name: string;
    video_url: string;
    sponsor_name: string;
    country_code?: string;
    reward_tp?: number;
    questions?: any[];
  }): Promise<any> {
    const response = await api.post('/admin/campaigns', data);
    return response.data;
  },

  async updateCampaign(campaignId: string, data: any): Promise<any> {
    const response = await api.patch(`/admin/campaigns/${campaignId}`, data);
    return response.data;
  },

  async deleteCampaign(campaignId: string): Promise<any> {
    const response = await api.delete(`/admin/campaigns/${campaignId}`);
    return response.data;
  },

  async activateCampaign(campaignId: string): Promise<any> {
    const response = await api.post(`/admin/campaigns/${campaignId}/activate`);
    return response.data;
  },

  async seedCampaigns(): Promise<any> {
    const response = await api.post('/admin/campaigns/seed');
    return response.data;
  },

  // ============ BANKS ============

  async getBanks(params?: {
    skip?: number;
    limit?: number;
    country_code?: string;
    search?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await api.get('/admin/banks', { params });
    return response.data;
  },

  async createBank(data: {
    name: string;
    code: string;
    country_code: string;
    logo_url?: string;
  }): Promise<any> {
    const response = await api.post('/admin/banks', data);
    return response.data;
  },

  async updateBank(bankId: number, data: any): Promise<any> {
    const response = await api.put(`/admin/banks/${bankId}`, data);
    return response.data;
  },

  async deleteBank(bankId: number): Promise<any> {
    const response = await api.delete(`/admin/banks/${bankId}`);
    return response.data;
  },

  // ============ SETTLEMENT ============

  async settleUser(userId: number, force?: boolean): Promise<any> {
    const response = await api.post(`/settlement/admin/settle-user/${userId}`, null, {
      params: { force: force || false },
    });
    return response.data;
  },

  async batchSettle(params?: { min_credits?: number; batch_size?: number }): Promise<any> {
    const response = await api.post('/settlement/admin/batch-settle', null, { params });
    return response.data;
  },

  // ============ BIO-KINETIC FRAUD DETECTION ============

  async getFraudConfig(): Promise<any> {
    const response = await api.get('/fraud-detection/config');
    return response.data;
  },

  async getFraudStats(hours?: number): Promise<any> {
    const response = await api.get('/fraud-detection/stats', { params: { hours } });
    return response.data;
  },

  async getFlaggedUsers(params?: { hours?: number; limit?: number }): Promise<any> {
    const response = await api.get('/fraud-detection/flagged-users', { params });
    return response.data;
  },

  // ============ FRAUD ENGINE (reason codes + device clusters) ============

  async getFraudReasonCodes(hours?: number): Promise<any> {
    const response = await api.get('/admin/fraud-engine/reason-codes', { params: { hours } });
    return response.data;
  },

  async getDeviceClusters(params?: { signal_type?: string; limit?: number }): Promise<any> {
    const response = await api.get('/admin/fraud-engine/device-clusters', { params });
    return response.data;
  },

  async getFraudAppeals(params?: { status?: string; limit?: number }): Promise<any> {
    const response = await api.get('/admin/fraud-engine/appeals', { params });
    return response.data;
  },

  async resolveFraudAppeal(appealId: number, approve: boolean, resolutionNotes?: string): Promise<any> {
    const response = await api.post(`/admin/fraud-engine/appeals/${appealId}/resolve`, {
      approve,
      resolution_notes: resolutionNotes,
    });
    return response.data;
  },

  async getFraudLiveSessions(params?: { limit?: number; min_risk?: number }): Promise<any> {
    const response = await api.get('/admin/fraud-engine/live-sessions', { params });
    return response.data;
  },

  async getFraudPeriodReport(period: 'weekly' | 'monthly'): Promise<any> {
    const response = await api.get('/admin/fraud-engine/reports', { params: { period } });
    return response.data;
  },

  // ============ COMPLIANCE ============

  async getComplianceReport(userId: number, days?: number): Promise<any> {
    const response = await api.get('/compliance/report', { params: { user_id: userId, days } });
    return response.data;
  },

  async getSkillClassification(userId: number, days?: number): Promise<any> {
    const response = await api.get(`/compliance/skill-classification/${userId}`, { params: { days } });
    return response.data;
  },

  // ============ ADDITIONAL ANALYTICS ============

  async getReviveAnalytics(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/revive-analytics', { params: { days } });
    return response.data;
  },

  async getRewardedAdsAnalytics(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/rewarded-ads-analytics', { params: { days } });
    return response.data;
  },

  async getComplianceAnalytics(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/compliance-analytics', { params: { days } });
    return response.data;
  },

  async getCurrencyAnalytics(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/currency-analytics', { params: { days } });
    return response.data;
  },

  async getSkillBasedGamingReport(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/skill-based-gaming-report', { params: { days } });
    return response.data;
  },

  async getAdEventsSummary(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/ad-events/summary', { params: { days } });
    return response.data;
  },

  async getAdEventsTimeseries(days?: number): Promise<any> {
    const response = await api.get('/admin/analytics/ad-events/timeseries', { params: { days } });
    return response.data;
  },

  // ============ MEDIA ============

  async getMediaStatus(): Promise<any> {
    const response = await api.get('/admin/media/status');
    return response.data;
  },

  async uploadMedia(file: File, folder?: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    const response = await api.post('/admin/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async listMedia(folder?: string): Promise<any> {
    const response = await api.get('/admin/media/list', { params: { folder } });
    return response.data;
  },

  async deleteMedia(key: string): Promise<any> {
    const response = await api.delete(`/admin/media/${key}`);
    return response.data;
  },

  // ============ SYSTEM ============

  async getVersion(): Promise<any> {
    const response = await api.get('/api/v1/version');
    return response.data;
  },

  // ============ DASHBOARD EXTENSIONS (funnel/retention/online) ============

  async getUserFunnel(days: number = 30, countryId?: number): Promise<any> {
    const response = await api.get('/admin/users/funnel', { params: { days, country_id: countryId } });
    return response.data;
  },

  async getRetentionMatrix(weeks: number = 8): Promise<any> {
    const response = await api.get('/admin/analytics/retention', { params: { weeks } });
    return response.data;
  },

  async getOnlineUsers(): Promise<any> {
    const response = await api.get('/admin/analytics/online-users');
    return response.data;
  },

  // ============ PROVIDERS HEALTH ============

  async getProvidersHealth(): Promise<any> {
    const response = await api.get('/admin/providers/health');
    return response.data;
  },

  // ============ AUDIT LOGS ============

  async getAuditEvents(params?: {
    limit?: number;
    type?: string;
    since?: string;
    actor_id?: number;
  }): Promise<any> {
    const response = await api.get('/admin/audit/events', { params });
    return response.data;
  },

  // ============ AIS CALLBACK LOGS ============

  async getAisCallbackLogs(params?: {
    provider?: string;
    days?: number;
    limit?: number;
  }): Promise<any> {
    const response = await api.get('/admin/ais/callback-logs', { params });
    return response.data;
  },

  // ============ LEADERBOARD ============

  async getLeaderboardLive(limit: number = 50): Promise<any> {
    const response = await api.get('/leaderboard/live', { params: { limit } });
    return response.data;
  },

  async getLeaderboardDaily(limit: number = 50): Promise<any> {
    const response = await api.get('/leaderboard/daily', { params: { limit } });
    return response.data;
  },

  async getLeaderboardWeekly(limit: number = 50): Promise<any> {
    const response = await api.get('/leaderboard/weekly', { params: { limit } });
    return response.data;
  },

  async getLeaderboardAllTime(limit: number = 50): Promise<any> {
    const response = await api.get('/leaderboard/all-time', { params: { limit } });
    return response.data;
  },

  // ============ BIO-KINETIC EXTENSIONS ============

  async getMoatExport(day?: string): Promise<Blob> {
    const response = await api.get('/fraud-detection/moat/export', {
      params: { day },
      responseType: 'blob',
    });
    return response.data;
  },

  async issueMoatPowChallenge(sessionId: string): Promise<any> {
    const response = await api.post('/fraud-detection/moat/pow-challenge', { session_id: sessionId });
    return response.data;
  },

  // ============ TASKS / OFFERWALL TEST SCENARIOS ============

  async getTaskTestScenario(): Promise<any> {
    const response = await api.get('/tasks/test/scenario');
    return response.data;
  },

  async setTaskTestScenario(scenario: string, ttlSeconds?: number): Promise<any> {
    const response = await api.post('/tasks/test/scenario', { scenario, ttl_seconds: ttlSeconds });
    return response.data;
  },

  async clearTaskTestScenario(): Promise<any> {
    const response = await api.delete('/tasks/test/scenario');
    return response.data;
  },

  // ============ PROMETHEUS METRICS ============

  async getPrometheusText(): Promise<string> {
    const response = await api.get('/metrics', { responseType: 'text' });
    return response.data as unknown as string;
  },

  // ============ FAQ / LEGAL (read-only convenience for dashboards) ============

  async getFaqs(): Promise<any> {
    const response = await api.get('/faqs');
    return response.data;
  },

  async listLegalDocuments(): Promise<any> {
    const response = await api.get('/legal/');
    return response.data;
  },

  // ============ Storytelling / Visa Brief ============

  async getNarrativeOverview(params?: {
    days?: number;
    include_test?: boolean;
    include_calibration?: boolean;
  }): Promise<any> {
    const response = await api.get('/admin/narrative/overview', { params });
    return response.data;
  },

  async getNarrativeGeography(params?: {
    include_test?: boolean;
    include_calibration?: boolean;
  }): Promise<any> {
    const response = await api.get('/admin/narrative/geography', { params });
    return response.data;
  },

  async getNarrativeCohorts(weeks: number = 8): Promise<any> {
    const response = await api.get('/admin/narrative/cohorts', { params: { weeks } });
    return response.data;
  },

  async listNarrativeItems(params?: {
    type?: 'press' | 'award' | 'partnership' | 'endorsement' | 'milestone';
    featured_only?: boolean;
    limit?: number;
  }): Promise<any> {
    const response = await api.get('/admin/narrative/items', { params });
    return response.data;
  },

  async createNarrativeItem(data: {
    type: 'press' | 'award' | 'partnership' | 'endorsement' | 'milestone';
    title: string;
    body?: string;
    source?: string;
    url?: string;
    image_url?: string;
    occurred_at?: string;
    is_featured?: boolean;
  }): Promise<any> {
    const response = await api.post('/admin/narrative/items', data);
    return response.data;
  },

  async updateNarrativeItem(id: number, data: Partial<{
    type: 'press' | 'award' | 'partnership' | 'endorsement' | 'milestone';
    title: string;
    body: string;
    source: string;
    url: string;
    image_url: string;
    occurred_at: string;
    is_featured: boolean;
  }>): Promise<any> {
    const response = await api.patch(`/admin/narrative/items/${id}`, data);
    return response.data;
  },

  async deleteNarrativeItem(id: number): Promise<any> {
    const response = await api.delete(`/admin/narrative/items/${id}`);
    return response.data;
  },

  // ============ Augmented Users (tags / notes / timeline / KYC / test flag) ============

  async listUsersRich(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
    tag?: string;
    kyc_tier?: string;
    acquisition_source?: string;
    country_id?: number;
    is_calibration_user?: boolean;
    is_test_account?: boolean;
    last_active_within_days?: number;
    sort?: string;
  }): Promise<any> {
    const response = await api.get('/admin/users/list', { params });
    return response.data;
  },

  async addUserTag(userId: number, tag: string): Promise<any> {
    const response = await api.post(`/admin/users/${userId}/tags`, { tag });
    return response.data;
  },

  async removeUserTag(userId: number, tag: string): Promise<any> {
    const response = await api.delete(`/admin/users/${userId}/tags/${encodeURIComponent(tag)}`);
    return response.data;
  },

  async listUserNotes(userId: number): Promise<any[]> {
    const response = await api.get(`/admin/users/${userId}/notes`);
    return response.data || [];
  },

  async createUserNote(userId: number, note: string): Promise<any> {
    const response = await api.post(`/admin/users/${userId}/notes`, { note });
    return response.data;
  },

  async getUserTimeline(userId: number): Promise<any> {
    const response = await api.get(`/admin/users/${userId}/timeline`);
    return response.data;
  },

  async setUserKycTier(userId: number, tier: string): Promise<any> {
    const response = await api.patch(`/admin/users/${userId}/kyc`, { kyc_tier: tier });
    return response.data;
  },

  async setUserTestFlag(userId: number, isTest: boolean): Promise<any> {
    const response = await api.patch(`/admin/users/${userId}/test-flag`, { is_test_account: isTest });
    return response.data;
  },

  // ============ KYC REVIEW (admin) ============

  async getKycQueue(params?: { limit?: number; offset?: number }): Promise<any> {
    const response = await api.get('/admin/kyc/queue', { params });
    return response.data;
  },

  async listKycSubmissions(params?: {
    user_id?: number;
    status?: string;
    id_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await api.get('/admin/kyc/submissions', { params });
    return response.data;
  },

  async getKycSubmission(submissionId: number): Promise<any> {
    const response = await api.get(`/admin/kyc/submissions/${submissionId}`);
    return response.data;
  },

  async approveKycSubmission(submissionId: number, data: {
    tier_granted?: string;
    note?: string;
  }): Promise<any> {
    const response = await api.post(`/admin/kyc/submissions/${submissionId}/approve`, data);
    return response.data;
  },

  async rejectKycSubmission(submissionId: number, rejection_reason: string): Promise<any> {
    const response = await api.post(`/admin/kyc/submissions/${submissionId}/reject`, {
      rejection_reason,
    });
    return response.data;
  },

  // Feature Flags
  async getFeatureFlags(): Promise<{ key: string; enabled: boolean; description: string | null; updated_at: string }[]> {
    const response = await api.get('/admin/feature-flags');
    return response.data;
  },

  async updateFeatureFlag(key: string, enabled: boolean): Promise<void> {
    await api.patch(`/admin/feature-flags/${key}`, { enabled });
  },

  // Ad Units (per-placement ad config served to the mobile app)
  async getAdUnits(): Promise<{
    placement: string;
    enabled: boolean;
    android_unit_id: string | null;
    ios_unit_id: string | null;
    description: string | null;
    updated_at: string;
  }[]> {
    const response = await api.get('/admin/ad-units');
    return response.data;
  },

  async updateAdUnit(
    placement: string,
    payload: { enabled?: boolean; android_unit_id?: string; ios_unit_id?: string }
  ): Promise<void> {
    await api.put(`/admin/ad-units/${placement}`, payload);
  },

  // Global Settings (AdminSetting table)
  async getAdminSettings(): Promise<AdminSetting[]> {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  async getAdminSetting(key: string): Promise<AdminSetting> {
    const response = await api.get<AdminSetting>(`/admin/settings/${key}`);
    return response.data;
  },

  async updateAdminSetting(key: string, value: unknown): Promise<AdminSetting> {
    if (key === DAILY_PASS_SETTING_KEY) {
      throw new Error(
        `Use setDailyPasses to update ${DAILY_PASS_SETTING_KEY}.`
      );
    }
    const response = await api.put<AdminSetting>(`/admin/settings/${key}`, { value });
    return response.data;
  },

  async setDailyPasses(count: number): Promise<{
    free_passes_per_day: number;
    updated_at?: string | null;
    updated_by?: string | null;
  }> {
    if (!isValidDailyPassCount(count)) {
      throw new Error(
        `Daily passes must be a whole number between ${DAILY_PASS_MIN} and ${DAILY_PASS_MAX}.`
      );
    }

    const response = await api.post<{ free_passes_per_day: number }>(
      '/api/v1/passes/admin/set-daily-passes',
      null,
      { params: { count } }
    );
    return response.data;
  },

  // Pro Workspace
  async listProQuestions(params: {
    category?: string;
    difficulty?: string;
    status?: string;
    limit?: number;
    offset?: number;
    [key: string]: string | number | undefined;
  }): Promise<{ total: number; items: any[] }> {
    const response = await api.get('/admin/pro/questions', { params });
    return response.data;
  },

  async createProQuestion(body: {
    question_text: string;
    options: string[];
    correct_answer: string;
    category: string;
    difficulty: string;
    country_relevance: string;
    explanation?: string;
    topic?: string;
    source_ref?: string;
  }): Promise<{ id: number; status: string }> {
    const response = await api.post('/admin/pro/questions', body);
    return response.data;
  },

  async updateProQuestion(id: number, body: {
    status?: string;
    question_text?: string;
    options?: string[]
    correct_answer?: string;
    explanation?: string;
    difficulty?: string;
    country_relevance?: string;
    topic?: string;
  }): Promise<{ id: number; status: string }> {
    const response = await api.patch(`/admin/pro/questions/${id}`, body);
    return response.data;
  },

  async listProSessions(params: {
    user_id?: number;
    status?: string;
    limit?: number;
    offset?: number;
    [key: string]: string | number | undefined;
  }): Promise<{ total: number; items: any[] }> {
    const response = await api.get('/admin/pro/sessions', { params });
    return response.data;
  },

  async bulkImportProQuestions(questions: object[]): Promise<{ created: number; ids: number[] }> {
    const response = await api.post('/admin/pro/questions/bulk', { questions });
    return response.data;
  },

  async getProStats(): Promise<{
    active_sessions: number;
    sessions_today: number;
    tp_awarded_today: number;
    questions_total: number;
    questions_live: number;
    questions_review: number;
    top_category: string;
  }> {
    const response = await api.get('/admin/pro/stats');
    return response.data;
  },

  async calibrateProQuestions(): Promise<{ updated: number; flagged_to_review: number; promoted_to_live: number; skipped: number }> {
    const response = await api.post('/admin/pro/questions/calibrate');
    return response.data;
  },

  async generateProQuestions(params: {
    category: string;
    difficulty: string;
    count: number;
    country_relevance: string;
  }): Promise<{ generated: number; inserted: number; skipped: number; errors: string[] }> {
    const response = await api.post('/admin/pro/questions/generate', params);
    return response.data;
  },

  async generateAllTiers(params: {
    category: string;
    country_relevance: string;
    count_per_tier: number;
  }): Promise<{ total_inserted: number; total_skipped: number; tiers: Record<string, { inserted: number; skipped: number; errors?: string[] }> }> {
    const response = await api.post('/admin/pro/questions/generate-all-tiers', params, { timeout: 120000 });
    return response.data;
  },

  async bulkApproveQuestions(params?: { category?: string; country_relevance?: string }): Promise<{ approved: number }> {
    const response = await api.post('/admin/pro/questions/bulk-approve', null, { params });
    return response.data;
  },
};
